import React, { useState, useCallback, useEffect } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { ResumeDoc } from './components/ResumeDoc'
import { FerlJobs } from './components/FerlJobs'
import { Notification } from './components/Notification'
import { RepoView } from './components/RepoView'
import { ArkHome } from './components/ArkHome'
import { ProfileForm } from './components/ProfileForm'
import { aiService } from './services/ai'
import { scrapeJob, CookieWallError } from './services/jobScraper'
import { atsOptimizedTemplate } from './templates/ats-optimized'
import { loadRepo, saveEntry } from './utils/repoStorage'
import { loadProfile, saveProfile } from './utils/profileStorage'
import { loadQueue, removeFromQueue, loadApplied, saveApplied } from './utils/queueStorage'
import './ark.css'

const CURRENT_RESUME_KEY = 'ark-current-resume'

const loadSavedTemplate = () => {
  try {
    const saved = localStorage.getItem(CURRENT_RESUME_KEY)
    if (saved) return JSON.parse(saved)
  } catch { /* ignore */ }
  return JSON.parse(JSON.stringify(atsOptimizedTemplate))
}

const STEPS = [
  { n: 1, label: 'Profile'  },
  { n: 2, label: 'Job URL'  },
  { n: 3, label: 'Generate' },
]

export default function ArkApp() {
  // ── view: 'home' | 'wizard' | 'repo' ─────────────────────────────────────
  const [view, setView]                 = useState('home')

  // ── wizard step ──────────────────────────────────────────────────────────
  const [step, setStep]                 = useState(1)
  const [showRepo, setShowRepo]         = useState(false)

  // ── job queue & applied ───────────────────────────────────────────────────
  const [queue, setQueue]               = useState(() => loadQueue())
  const [applied, setApplied]           = useState(() => loadApplied())

  // ── step 1: profile ───────────────────────────────────────────────────────
  const [profile, setProfile]           = useState(() => loadProfile())
  const [profileWarnings, setProfileWarnings] = useState([])

  // ── step 2: job url ───────────────────────────────────────────────────────
  const [jobUrl, setJobUrl]             = useState('')
  const [fetching, setFetching]         = useState(false)
  const [scraped, setScraped]           = useState(null)
  const [fetchError, setFetchError]     = useState(null)
  const [cookieWall, setCookieWall]     = useState(false)
  const [manualDesc, setManualDesc]     = useState('')
  const [showBrowse, setShowBrowse]     = useState(false)

  // ── step 3: generate & export ─────────────────────────────────────────────
  const [template, setTemplate]         = useState(loadSavedTemplate)
  const [isGenerated, setIsGenerated]   = useState(false)
  const [generating, setGenerating]     = useState(false)
  const [generateError, setGenerateError] = useState(null)
  const [entryTitle, setEntryTitle]     = useState('')
  const [activeEntry, setActiveEntry]   = useState(null)
  const [repo, setRepo]                 = useState(() => loadRepo())
  const [notification, setNotification] = useState(null)

  useEffect(() => {
    aiService.init().catch(err =>
      setNotification({ message: `AI: ${err.message}`, type: 'error' })
    )
  }, [])

  const handleTemplateChange = useCallback((t) => {
    setTemplate(t)
    try { localStorage.setItem(CURRENT_RESUME_KEY, JSON.stringify(t)) } catch { }
  }, [])

  // ── step 1 → 2 ────────────────────────────────────────────────────────────
  const handleContinueStep1 = () => {
    const warns = []
    if (!profile.name?.trim())  warns.push({ msg: 'Name is required', blocking: true })
    if (!profile.email?.trim()) warns.push({ msg: 'Email is required', blocking: true })
    if (!profile.experience?.length)
      warns.push({ msg: 'No work experience added — ark will still generate a resume', blocking: false })

    setProfileWarnings(warns)
    if (warns.some(w => w.blocking)) return
    saveProfile(profile)
    setStep(2)
    setView('wizard')
    window.scrollTo(0, 0)
  }

  // ── step 2: fetch job ─────────────────────────────────────────────────────
  const handleFetch = async () => {
    if (!jobUrl.trim()) { setFetchError('Paste a job URL first.'); return }
    setFetchError(null); setCookieWall(false); setFetching(true); setScraped(null)
    try {
      const job = await scrapeJob(jobUrl.trim())
      setScraped(job)
      setEntryTitle([job.company, job.title].filter(Boolean).join(' – '))
    } catch (err) {
      if (err instanceof CookieWallError) {
        setCookieWall(true)
        const autoTitle = [err.company, err.title].filter(Boolean).join(' – ')
        if (autoTitle.trim()) setEntryTitle(autoTitle)
        setScraped({ url: jobUrl.trim(), title: err.title, company: err.company, description: '', rawText: '' })
      } else {
        setFetchError(err instanceof Error ? err.message : 'Could not fetch that page.')
      }
    } finally { setFetching(false) }
  }

  // ── step 2 → 3 (generate) ─────────────────────────────────────────────────
  const handleGenerate = useCallback(async () => {
    if (!scraped) { setFetchError('Fetch a job URL first.'); return }
    if (cookieWall && !manualDesc.trim()) { setFetchError('Paste the job description first.'); return }
    setStep(3)
    window.scrollTo(0, 0)
    setGenerating(true); setGenerateError(null); setIsGenerated(false)
    try {
      const job = cookieWall ? { ...scraped, description: manualDesc } : scraped
      const generated = await aiService.generateResumeFromProfile(
        profile, job.description, job.title, job.company
      )
      handleTemplateChange(generated)
      setIsGenerated(true)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Generation failed.'
      setGenerateError(
        msg.includes('429') || msg.includes('quota')
          ? 'OpenAI quota exceeded — add billing at platform.openai.com/settings/billing'
          : msg
      )
    } finally { setGenerating(false) }
  }, [profile, scraped, cookieWall, manualDesc, handleTemplateChange])

  // ── save to ferl repo ─────────────────────────────────────────────────────
  const handleSave = useCallback(() => {
    if (!entryTitle.trim()) {
      setNotification({ message: 'Add a title before saving.', type: 'error' })
      return
    }
    const now = new Date().toISOString()
    const entry = {
      id: activeEntry?.id || uuidv4(),
      title: entryTitle.trim(), company: '', jobTitle: '',
      status: activeEntry?.status || 'draft',
      jobDescription: '', template,
      appliedDate: activeEntry?.appliedDate,
      created: activeEntry?.created || now, updated: now,
    }
    saveEntry(entry); setActiveEntry(entry)
    setRepo(prev => ({ ...prev, [entry.id]: entry }))
    setNotification({ message: `"${entry.title}" saved to Ferl Repo.`, type: 'success' })
  }, [activeEntry, entryTitle, template])

  // ── open saved entry ──────────────────────────────────────────────────────
  const openEntry = (entry) => {
    setActiveEntry(entry)
    handleTemplateChange(JSON.parse(JSON.stringify(entry.template)))
    setEntryTitle(entry.title)
    setIsGenerated(true)
    setGenerateError(null)
    setShowRepo(false)
    setStep(3)
    setView('wizard')
    window.scrollTo(0, 0)
  }

  // ── start wizard from queued job ──────────────────────────────────────────
  const handleStartJob = (job) => {
    setJobUrl(job.job_url || '')
    setScraped(null)
    setFetchError(null)
    setCookieWall(false)
    setManualDesc('')
    setEntryTitle(
      [job.company_name, job.job_title].filter(Boolean).join(' – ')
    )
    setActiveEntry(null)
    setIsGenerated(false)
    setStep(2)
    setView('wizard')
    window.scrollTo(0, 0)
  }

  // ── mark resume as applied ────────────────────────────────────────────────
  const handleMarkApplied = useCallback(() => {
    if (!entryTitle.trim() || !isGenerated) return
    const id = uuidv4()
    const entry = {
      id,
      jobTitle: scraped?.title || entryTitle,
      company: scraped?.company || '',
      resumeTitle: entryTitle,
      appliedAt: new Date().toISOString(),
    }
    const next = saveApplied(entry)
    setApplied(next)
    setNotification({ message: 'Marked as applied and tracked.', type: 'success' })
  }, [entryTitle, isGenerated, scraped])

  return (
    <div className="ark-root">
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      {/* ── Topbar ── */}
      <div className="ark-topbar">
        <div className="ark-topbar-left">
          <button
            className={`ark-logo-btn${view === 'home' ? ' active' : ''}`}
            onClick={() => { setView('home'); setShowRepo(false) }}
          >
            ark
          </button>
        </div>

        {/* Step indicator — only in wizard view */}
        {view === 'wizard' && (
          <div className="ark-wizard-steps">
            {STEPS.map(({ n, label }, i) => (
              <React.Fragment key={n}>
                <button
                  className={`ark-wstep${step === n ? ' active' : ''}${step > n ? ' done' : ''}`}
                  onClick={() => step > n && setStep(n)}
                  disabled={step <= n}
                >
                  <span className="ark-wstep-n">{step > n ? '✓' : n}</span>
                  <span className="ark-wstep-label">{label}</span>
                </button>
                {i < STEPS.length - 1 && <span className="ark-wstep-sep">─</span>}
              </React.Fragment>
            ))}
          </div>
        )}

        <div className="ark-topbar-right">
          <button
            className={`ark-nav-btn${view === 'home' ? ' active' : ''}`}
            onClick={() => { setView('home'); setShowRepo(false) }}
          >
            home
          </button>
          <button
            className={`ark-nav-btn${view === 'wizard' || showRepo ? '' : ''}`}
            onClick={() => { setView('wizard'); setStep(1); setShowRepo(false) }}
          >
            new resume
          </button>
          {view === 'wizard' && step === 3 && isGenerated && (
            <button className="ark-print-btn" onClick={() => window.print()}>
              ⌘P  print / pdf
            </button>
          )}
        </div>
      </div>

      {/* ── Home dashboard ── */}
      {view === 'home' && (
        <ArkHome
          queue={queue}
          onQueueChange={setQueue}
          repo={repo}
          applied={applied}
          onAppliedChange={setApplied}
          onStartJob={handleStartJob}
          onOpenResume={openEntry}
          onNewResume={() => { setStep(1); setView('wizard'); setShowRepo(false) }}
        />
      )}

      {/* ── Step 1: Profile ── */}
      {view === 'wizard' && !showRepo && step === 1 && (
        <div className="ark-step-page">
          <ProfileForm
            profile={profile}
            onSave={(p) => { saveProfile(p); setProfile(p) }}
            onAutoSave={(p) => setProfile(p)}
          />

          {profileWarnings.length > 0 && (
            <div className="ark-validation-box">
              {profileWarnings.map((w, i) => (
                <div key={i} className={`ark-validation-item ${w.blocking ? 'error' : 'warn'}`}>
                  {w.blocking ? '✗' : '⚠'} {w.msg}
                </div>
              ))}
            </div>
          )}

          <div className="ark-step-actions">
            <button className="ark-continue-btn" onClick={handleContinueStep1}>
              Continue to Job URL →
            </button>
          </div>
        </div>
      )}

      {/* ── Step 2: Job URL ── */}
      {view === 'wizard' && !showRepo && step === 2 && (
        <div className="ark-step-page">
          <div className="ark-step-centered">
            <div className="ark-step-header">
              <h2>Find a job to apply for</h2>
              <p>Paste a specific job posting URL below — not just a careers home page.</p>
            </div>

            <div className="ark-job-card">
              <label className="ark-field-label">Job Posting URL</label>
              <div className="ark-url-row">
                <input
                  className="ark-url-input"
                  placeholder="https://jobs.lever.co/stripe/..."
                  value={jobUrl}
                  onChange={e => { setJobUrl(e.target.value); setFetchError(null) }}
                  onKeyDown={e => e.key === 'Enter' && handleFetch()}
                />
                <button
                  className="ark-fetch-btn"
                  onClick={handleFetch}
                  disabled={fetching}
                >
                  {fetching ? '…' : 'Fetch'}
                </button>
              </div>

              {fetchError && <div className="ark-field-error">{fetchError}</div>}

              {scraped && !cookieWall && (
                <div className="ark-scraped-ok">
                  <span className="ark-scraped-check">✓</span>
                  <strong>{scraped.title || 'Job'}</strong>
                  {scraped.company && <span> at {scraped.company}</span>}
                  <span className="ark-scraped-ready"> — ready to generate</span>
                </div>
              )}

              {cookieWall && (
                <>
                  <div className="ark-cookiewall-notice">
                    Cookie wall — paste the job description below.
                  </div>
                  <label className="ark-field-label" style={{ marginTop: '.75rem' }}>
                    Job Description
                  </label>
                  <textarea
                    className="ark-desc-textarea"
                    value={manualDesc}
                    onChange={e => setManualDesc(e.target.value)}
                    placeholder="Paste the full job description here..."
                  />
                </>
              )}

              {scraped && !cookieWall && (
                <>
                  <label className="ark-field-label" style={{ marginTop: '.75rem' }}>
                    Job Description{' '}
                    <span style={{ color: '#7f7f7f', fontWeight: 400, textTransform: 'none' }}>
                      — edit if needed
                    </span>
                  </label>
                  <textarea
                    className="ark-desc-textarea"
                    value={scraped.description}
                    onChange={e => setScraped({ ...scraped, description: e.target.value })}
                  />
                </>
              )}
            </div>

            {/* Step actions — above the browser */}
            <div className="ark-step-actions">
              <button className="ark-back-btn" onClick={() => setStep(1)}>← Back</button>
              <button
                className="ark-continue-btn"
                onClick={handleGenerate}
                disabled={!scraped}
                style={!scraped ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
              >
                Generate Resume →
              </button>
            </div>

            {/* Browse Ferl Jobs */}
            <button
              className={`ark-browse-toggle-btn${showBrowse ? ' open' : ''}`}
              onClick={() => setShowBrowse(b => !b)}
            >
              {showBrowse ? '↑ Hide Ferl Jobs' : 'Browse Ferl Jobs to find a role →'}
            </button>
          </div>

          {showBrowse && (
            <div className="ark-browse-panel">
              <FerlJobs wizardMode />
            </div>
          )}
        </div>
      )}

      {/* ── Step 3: Generate & Export ── */}
      {view === 'wizard' && !showRepo && step === 3 && (
        <div className="ark-step-page">
          {generating && (
            <div className="ark-generating-banner">
              <div className="ark-generating-dot" />
              <div className="ark-generating-dot" style={{ animationDelay: '.15s' }} />
              <div className="ark-generating-dot" style={{ animationDelay: '.3s' }} />
              <span>Writing your tailored resume…</span>
            </div>
          )}

          {generateError && (
            <div className="ark-generate-error">{generateError}</div>
          )}

          {isGenerated && (
            <div className="ark-export-bar">
              <input
                className="ark-title-input"
                value={entryTitle}
                onChange={e => setEntryTitle(e.target.value)}
                placeholder="Resume title (e.g. Stripe – Engineer)"
              />
              <div className="ark-export-btns">
                <button className="ark-save-btn"  onClick={handleSave}>
                  Save to Ferl Repo
                </button>
                <button className="ark-applied-btn" onClick={handleMarkApplied}>
                  ✓ Mark as Applied
                </button>
                <button className="ark-pdf-btn" onClick={() => window.print()}>
                  ⬇ Download PDF
                </button>
                <button className="ark-drive-btn" disabled title="Coming soon">
                  Google Drive — soon
                </button>
              </div>
            </div>
          )}

          <ResumeDoc template={template} onChange={handleTemplateChange} />

          <div className="ark-step-actions" style={{ padding: '1rem 1.5rem 2rem' }}>
            <button className="ark-back-btn" onClick={() => setStep(2)}>← Change Job</button>
          </div>
        </div>
      )}
    </div>
  )
}
